import React from 'react';
import { resolveText } from '../utils/clientTexts';
import PlatformFooter from './PlatformFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LiveClientPreview
 *
 * A faithful, miniature reflection of the public ClientView page.
 * Mirrors the EXACT same text-resolution chain ClientView uses
 * (resolveText → branding[lang] → template[lang] → template.en → i18n base),
 * so what a studio sees here is what their client will see.
 *
 * Props:
 *   colors                 – { page, section, border, text, textLight, accent }
 *   brandName              – string
 *   logoUrl                – string | null
 *   brandingDisplayMode    – 'logo_and_name' | 'logo_only' | 'name_only'
 *   logoSize               – 'S' | 'M' | 'L'
 *   welcomeByLang          – full per-lang override bucket (form state)
 *   templateTexts          – per-lang template defaults from /api/auth/branding
 *   footerTexts            – { custom_footer_text, custom_footer_subtext,
 *                             custom_footer_link_label, custom_footer_link_url }
 *   editLang               – 'en' | 'pl' | 'de'
 *   t                      – translation bundle (provides i18n base defaults)
 */
const LiveClientPreview = ({
  colors,
  brandName,
  logoUrl,
  brandingDisplayMode = 'logo_and_name',
  logoSize = 'M',
  welcomeByLang = {},
  templateTexts = {},
  footerTexts = {},
  editLang = 'en',
  t = {},
}) => {
  const lang = editLang || 'en';

  // Use the SAME resolution chain ClientView uses, with i18n base defaults
  // as the final fallback — exactly what the client will actually see.
  const pick = (key, fallback) => resolveText({
    branding: welcomeByLang,
    template: templateTexts,
    lang,
    key,
    fallback,
  });

  const welcomeTitle = pick('welcome_title', t.welcome || 'Welcome.');
  const welcomeSubtitle = pick('welcome_subtitle', t.client_intro || '');
  const welcomeDescription = pick('welcome_description', t.client_hint || '');
  const customMessage = pick('custom_message', '');
  // Instruction box removed — Welcome is the single introduction.

  const sizeMap = { S: '20px', M: '32px', L: '88px' };
  const maxWidthMap = { S: '50%', M: '60%', L: '78%' };
  const logoH = sizeMap[logoSize] || '32px';
  const logoMaxW = maxWidthMap[logoSize] || '60%';
  const hasLogo = !!logoUrl;
  const showLogo = hasLogo && brandingDisplayMode !== 'name_only';
  const showName = brandingDisplayMode !== 'logo_only' || !hasLogo;

  const langLabel = lang.toUpperCase();
  const fName = brandName || t.studio_name || 'Maestra';

  // Helper for newline-aware text rendering
  const renderMultiLine = (text, key) => (text || '').split('\n').map((line, i) => (
    <React.Fragment key={`${key}-${i}`}>
      {line}
      {i < (text || '').split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div
      className="lcp-frame"
      data-testid="live-client-preview"
      style={{
        background: colors.page,
        border: `1px solid ${colors.border}`,
        borderRadius: '3px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(28, 22, 14, 0.04)',
        position: 'relative',
        color: colors.text,
      }}
    >
      {/* Tiny "preview language" tag, top-right corner */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '8px',
          right: '10px',
          zIndex: 2,
          fontFamily: 'Playfair Display, serif',
          fontSize: '8px',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: colors.textLight,
          opacity: 0.55,
        }}
      >
        {langLabel}
      </span>

      {/* Header / brand bar — mirrors ClientView <header className="client-header"> */}
      <header
        style={{
          padding: '24px 18px 20px',
          textAlign: 'center',
          background: colors.section,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {showLogo && (
          <div style={{ marginBottom: showName ? '6px' : '0' }}>
            <img
              src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}/api/images/${logoUrl}`}
              alt=""
              style={{
                height: 'auto',
                maxHeight: logoH,
                maxWidth: logoMaxW,
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
                opacity: 0.92,
              }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}
        {showName && (
          <p
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: showLogo ? '8.5px' : '9px',
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: colors.textLight,
              margin: 0,
            }}
          >
            {fName}
          </p>
        )}

        {/* Welcome title (h1 in ClientView — Playfair, larger) */}
        <h3
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: 1.25,
            color: colors.text,
            margin: '18px 0 0',
            letterSpacing: '0.005em',
          }}
        >
          {welcomeTitle}
        </h3>

        {/* Description (= client_intro in ClientView) */}
        {welcomeSubtitle && (
          <p
            style={{
              fontSize: '11px',
              lineHeight: 1.65,
              color: colors.textLight,
              margin: '10px auto 0',
              maxWidth: '300px',
              whiteSpace: 'pre-line',
            }}
          >
            {renderMultiLine(welcomeSubtitle, 'sub')}
          </p>
        )}

        {/* Italic hint under description (= client_hint in ClientView) */}
        {welcomeDescription && (
          <p
            style={{
              fontSize: '10.5px',
              fontStyle: 'italic',
              color: colors.textLight,
              opacity: 0.82,
              margin: '10px 0 0',
            }}
          >
            {welcomeDescription}
          </p>
        )}

        {/* Custom message — boxed, cream-light feel like ClientView */}
        {customMessage && (
          <div
            style={{
              marginTop: '14px',
              padding: '10px 14px',
              background: colors.page,
              border: `1px solid ${colors.border}`,
              maxWidth: '300px',
              margin: '14px auto 0',
            }}
          >
            <p style={{
              fontSize: '11px',
              color: colors.text,
              lineHeight: 1.55,
              margin: 0,
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
            }}>
              {customMessage}
            </p>
          </div>
        )}
      </header>

      {/* Instruction block removed — the Welcome stack above is the
          single introduction, mirroring the real ClientView. */}

      {/* Mini gallery — 3 image tiles in atmosphere tones */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '6px',
          padding: '0 22px 22px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              aspectRatio: '3 / 4',
              background: `linear-gradient(160deg, ${colors.section} 0%, ${colors.border} 100%)`,
              border: `1px solid ${colors.border}`,
              borderRadius: '1px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute', inset: 4,
                border: `1px solid ${colors.border}`,
                opacity: 0.45,
              }}
            />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '16px 18px 18px',
          textAlign: 'center',
          background: colors.section,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        {footerTexts.custom_footer_text ? (
          <>
            <p
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '11px',
                letterSpacing: '0.18em',
                color: colors.text,
                margin: '0 0 3px',
                textTransform: 'uppercase',
              }}
            >
              {footerTexts.custom_footer_text}
            </p>
            {footerTexts.custom_footer_subtext && (
              <p
                style={{
                  fontSize: '9px',
                  letterSpacing: '0.08em',
                  color: colors.textLight,
                  margin: '0 0 4px',
                }}
              >
                {footerTexts.custom_footer_subtext}
              </p>
            )}
            {footerTexts.custom_footer_link_label && footerTexts.custom_footer_link_url && (
              <p
                style={{
                  fontSize: '9px',
                  color: colors.textLight,
                  textDecoration: 'underline',
                  margin: '0 0 6px',
                }}
              >
                {footerTexts.custom_footer_link_label}
              </p>
            )}
            <p
              style={{
                fontSize: '7.5px',
                letterSpacing: '0.2em',
                color: colors.textLight,
                opacity: 0.55,
                margin: '4px 0 0',
              }}
            >
              created with maestra.space
            </p>
          </>
        ) : (
          <>
            {/* Default Maestra system footer (matches the global one shown to clients) */}
            <PlatformFooter
              align="center"
              style={{ color: colors.textLight }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LiveClientPreview;
